import { scaleLinear } from '@visx/scale';
import type { ChartModel } from './chartModel';
import type { Extent } from './extent';
import { tickLabel, tickStepFor, timeTicks } from './time';

/** A numeric linear scale, as `@visx/scale`'s `scaleLinear` builds it. */
export type LinearScale = ReturnType<typeof scaleLinear<number>>;

/** Space around each chart's plot area, in pixels; the left margin grows to fit the widest y label. */
export const CHART_MARGIN = { top: 8, right: 4, bottom: 24, left: 40 };

/** Gap between a y label and the plot area, in pixels. */
export const Y_LABEL_GAP = 8;

const Y_TICK_COUNT = 6;
const Y_LABEL_FALLBACK_CHAR_PX = 7.5;

/** Measures a y label's rendered width in pixels. */
export type MeasureText = (text: string) => number;

/** A per-character width guess, wide enough for the y-label font, for when the text can't be measured. */
export const estimateTextWidth: MeasureText = (text) =>
  text.length * Y_LABEL_FALLBACK_CHAR_PX;

/** Height of each chart's plot area, in pixels (Figma V12). */
export const PLOT_HEIGHT = 274;

/** Horizontal room each x-axis label needs, in pixels. */
export const X_LABEL_WIDTH = 72;

/** How an x-axis label lines up with its tick: centred, or flush with the plot edge it would overhang. */
export type XTickAnchor = 'start' | 'middle' | 'end';

/** One x-axis label. */
export interface XTick {
  at: number;
  label: string;
  anchor: XTickAnchor;
}

/**
 * X-axis labels on round local times in the timezone, as many as fit across `plotWidth`. A label centred on its
 * tick that would overhang either end of the plot is anchored to that end instead.
 */
export const xTicksFor = (
  window: Extent,
  timeZone: string,
  plotWidth: number,
  measure: MeasureText = estimateTextWidth
): XTick[] => {
  const maxTicks = Math.max(2, Math.floor(plotWidth / X_LABEL_WIDTH));
  const step = tickStepFor(window[0], window[1], maxTicks);
  const span = window[1] - window[0];
  return timeTicks(window[0], window[1], timeZone, step).map((at) => {
    const label = tickLabel(at, timeZone, step);
    const x = span > 0 ? ((at - window[0]) / span) * plotWidth : 0;
    const half = measure(label) / 2;
    const anchor: XTickAnchor =
      x < half ? 'start' : x > plotWidth - half ? 'end' : 'middle';
    return { at, label, anchor };
  });
};

/** One y-axis label. */
export interface YTick {
  value: number;
  label: string;
}

/** A chart's y scale over the plot height, rounded to tidy ticks, with a label per tick. */
export interface YAxis {
  scale: LinearScale;
  ticks: YTick[];
}

/** The y axis for a domain, labelling each tick with its value and unit. */
export const yAxisFor = (domain: Extent, unit: string): YAxis => {
  const scale = scaleLinear<number>({
    domain: [domain[0], domain[1]],
    range: [PLOT_HEIGHT, 0],
    nice: Y_TICK_COUNT,
  });
  const format = scale.tickFormat(Y_TICK_COUNT);
  return {
    scale,
    ticks: scale
      .ticks(Y_TICK_COUNT)
      .map((value) => ({ value, label: `${format(value)}${unit}` })),
  };
};

/** The left margin that fits the widest y label across every chart, so the stack shares one x scale. */
export const leftMarginFor = (
  axes: readonly YAxis[],
  measure: MeasureText = estimateTextWidth
): number => {
  let widest = 0;
  for (const axis of axes) {
    for (const tick of axis.ticks) {
      widest = Math.max(widest, measure(tick.label));
    }
  }
  return Math.max(CHART_MARGIN.left, Math.ceil(widest) + Y_LABEL_GAP);
};

/** The shared geometry of a chart stack: each chart's y axis, one left margin, one x scale and its ticks. */
export interface StackGeometry {
  yAxes: YAxis[];
  marginLeft: number;
  xScale: LinearScale;
  xTicks: XTick[];
}

/** Lays out the charts so they share one x scale, with the margin fitting the widest y label. */
export const stackGeometry = (
  models: readonly ChartModel[],
  window: Extent,
  width: number,
  timeZone: string,
  measure: MeasureText = estimateTextWidth
): StackGeometry => {
  const yAxes = models.map((model) =>
    yAxisFor(model.domain, model.measure.unit)
  );
  const marginLeft = leftMarginFor(yAxes, measure);
  return {
    yAxes,
    marginLeft,
    xScale: scaleLinear<number>({
      domain: [window[0], window[1]],
      range: [marginLeft, width - CHART_MARGIN.right],
    }),
    xTicks: xTicksFor(
      window,
      timeZone,
      width - marginLeft - CHART_MARGIN.right,
      measure
    ),
  };
};
